import { useForm } from 'react-hook-form'

import { useEffect } from 'react'

import { useEnterSubmit } from '@/hooks/ui'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { SwitchInput, TextInput } from '@/components/atoms'

import { AttributeModel } from '@/types'
import { Spinner } from '@/components/ui/spinner'

interface AttributeDialogProps {
  open: boolean
  mode: 'create' | 'edit' | 'view'
  editAttribute: AttributeModel.Attribute | null
  closeDialog: () => void
  onCreate: (data: AttributeFormData) => void
  onUpdate: (id: string, data: AttributeFormData) => void
  loading?: boolean
}

interface AttributeFormData {
  name: string
  is_color: boolean
  status: 'active' | 'inactive'
}

export const AttributeDialog = ({
  open,
  mode,
  editAttribute,
  closeDialog,
  onCreate,
  onUpdate,
  loading = false,
}: AttributeDialogProps) => {
  const {
    handleSubmit,
    watch,
    reset,
    formState: { errors },
    control,
  } = useForm<AttributeFormData>({
    defaultValues: {
      name: '',
      is_color: false,
      status: 'active',
    },
  })

  const formValues = watch()

  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && editAttribute) {
      reset({
        name: editAttribute.name,
        is_color: !!editAttribute.is_color,
        status: editAttribute.status,
      })
    } else {
      reset({
        name: '',
        is_color: false,
        status: 'active',
      })
    }
  }, [mode, editAttribute, reset])

  const onSubmit = async (data: AttributeFormData) => {
    if (mode === 'create') {
      onCreate(data)
    } else {
      if (!editAttribute?._id) return
      onUpdate(editAttribute._id, data)
    }
  }

  useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? 'Add attribute'
              : mode === 'edit'
                ? 'Edit attribute'
                : 'Attribute details'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Create a new product attribute.'
              : mode === 'edit'
                ? 'Update attribute information.'
                : 'View attribute details.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <TextInput
            control={control}
            name="name"
            label="Name"
            placeholder="e.g., Color"
            disabled={mode === 'view'}
            rules={{ required: 'Name is required' }}
            errors={errors}
          />

          <SwitchInput
            control={control}
            name="is_color"
            label="Color attribute"
            description="Values can have a color swatch."
            disabled={mode === 'view'}
          />

          <SwitchInput
            control={control}
            name="status"
            label="Status"
            description={
              formValues?.status === 'active' ? 'Attribute is visible' : 'Attribute is hidden'
            }
            disabled={mode === 'view'}
            activeValue="active"
            inactiveValue="inactive"
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={closeDialog} disabled={loading}>
            {mode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {mode !== 'view' && (
            <Button type="button" disabled={loading} onClick={handleSubmit(onSubmit)}>
              {loading && <Spinner />}
              {mode === 'create' ? 'Create' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
